import { hasToken, isTokenExpired } from '@utils';
import InspectionService from '../inspection/inspectionService';
import {
  blockAnswerDraft,
  blockPhotoDraft,
  clearAnswerDraft,
  deletePhotoDraft,
  loadAllAnswerDrafts,
  loadAllPhotoDrafts,
} from './inspectionDrafts';

/**
 * Sends what an inspection left behind on this device, from anywhere in the app.
 *
 * The drawer retries too, but only while it is open, and that is not where the risk is. A technician
 * fills a checklist in on site with no signal, closes it, and drives back - they never saw the notice
 * and have no reason to open that inspection again. If sending depended on them returning to it, the
 * work would simply never arrive.
 */

/**
 * Inspections a screen is currently working on. The drawer runs its own queue for these and reports
 * on it; two senders on one inspection would be safe - the API upserts - but the screen would be
 * fighting something it cannot see.
 */
const claimed = new Set<string>();

export const claimInspection = (inspectionId: string) => claimed.add(inspectionId);
export const releaseInspection = (inspectionId: string) => claimed.delete(inspectionId);

let running = false;

export interface SyncResult {
  answers: number;
  photos: number;
}

/**
 * A refusal, as opposed to a connection that is not there.
 *
 * The server answered, so the network is fine and asking again will get the same answer: the
 * inspection is gone, or belongs to another garage. 401 is left out - that is the session, which the
 * app handles elsewhere - and so are 408 and 429, which mean "not now" rather than "no".
 */
const isRefusal = (error: unknown): boolean => {
  const status = (error as { response?: { status?: number } })?.response?.status;

  if (status === undefined) return false;

  return status >= 400 && status < 500 && status !== 401 && status !== 408 && status !== 429;
};

/** One pass. Safe to call often - it returns immediately when there is nothing to do. */
export const syncPendingDrafts = async (): Promise<SyncResult> => {
  const nothing: SyncResult = { answers: 0, photos: 0 };

  if (running) return nothing;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return nothing;

  // No session means every request would 401 and tip the app onto the login screen. The drafts keep.
  if (!hasToken() || isTokenExpired()) return nothing;

  running = true;

  const result: SyncResult = { answers: 0, photos: 0 };

  try {
    const answerDrafts = (await loadAllAnswerDrafts()).filter(
      (draft) => !draft.blockedAt && !claimed.has(draft.inspectionId)
    );

    for (const draft of answerDrafts) {
      try {
        await InspectionService.createResponses({
          id: draft.inspectionId,
          responses: draft.answers,
        });

        await clearAnswerDraft(draft.inspectionId);
        result.answers += 1;
      } catch (error) {
        if (isRefusal(error)) {
          await blockAnswerDraft(draft.inspectionId);
          continue;
        }

        // Connection trouble: the rest will fail the same way, so stop and let the next pass try.
        break;
      }
    }

    const photoDrafts = (await loadAllPhotoDrafts()).filter(
      (draft) => !draft.blockedAt && !claimed.has(draft.inspectionId)
    );

    // One at a time on purpose. Whatever brought the connection back is not necessarily a good one,
    // and forty photos at once is how it goes away again.
    for (const draft of photoDrafts) {
      try {
        await InspectionService.uploadResponseDocument({
          inspectionId: draft.inspectionId,
          subCategoryItemId: draft.subCategoryItemId,
          name: draft.name,
          contentType: draft.contentType,
          content: draft.content,
        });

        await deletePhotoDraft(draft.key);
        result.photos += 1;
      } catch (error) {
        if (isRefusal(error)) {
          await blockPhotoDraft(draft.key);
          continue;
        }

        break;
      }
    }
  } finally {
    running = false;
  }

  return result;
};
